/**
 * ShortAnswerBox
 * Written-response component for Sage Homeschool & Church Bible College.
 * Shown after a lesson completes (when enabled by essay_mode on the class).
 *
 * Features:
 *  - Word-count live counter
 *  - Paste detection (flags but doesn't block)
 *  - Typing duration tracking
 *  - Submits to ai-sage-grader edge function
 *  - Shows score, rubric feedback, follow-up question
 */
import { useState, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { PenLine, Send, CheckCircle, AlertTriangle, Loader2, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/integrations/supabase/client'

interface ShortAnswerBoxProps {
    prompt: string
    classId: string
    moduleId: number
    lessonId: number
    wordMin?: number
    wordMax?: number
    maxScore?: number
    /** Pass an assignment_id from university_assignments if one exists for this lesson */
    assignmentId?: string
    onComplete?: (score: number, maxScore: number) => void
    /** colour accent */
    accentColor?: string
    sageSource?: 'homeschool' | 'church' | 'bible_college' | 'elite'
}

export function ShortAnswerBox({
    prompt, classId, moduleId, lessonId,
    wordMin = 50, wordMax = 300, maxScore = 10,
    assignmentId, onComplete,
    accentColor = '#c8a84b',
    sageSource = 'homeschool',
}: ShortAnswerBoxProps) {
    const { t, i18n } = useTranslation()
    const [text, setText] = useState('')
    const [status, setStatus] = useState<'idle' | 'submitting' | 'done' | 'error'>('idle')
    const [result, setResult] = useState<any>(null)
    const [pasteDetected, setPaste] = useState(false)
    const startRef = useRef<number>(Date.now())
    const mounted = useRef(true)

    const wordCount = text.trim() === '' ? 0 : text.trim().split(/\s+/).length
    const meetsMin = wordCount >= wordMin
    const meetsMax = wordCount <= wordMax

    const handlePaste = useCallback(() => {
        setPaste(true)
    }, [])

    const handleSubmit = async () => {
        if (!meetsMin || !meetsMax || status === 'submitting') return
        setStatus('submitting')
        const typingSeconds = Math.round((Date.now() - startRef.current) / 1000)

        try {
            const { data: { session } } = await supabase.auth.getSession()
            if (!session) throw new Error('Not authenticated')

            const res = await supabase.functions.invoke('ai-sage-grader', {
                body: {
                    class_id: classId,
                    module_id: moduleId,
                    lesson_id: lessonId,
                    prompt,
                    response_text: text,
                    word_min: wordMin,
                    word_max: wordMax,
                    max_score: maxScore,
                    paste_detected: pasteDetected,
                    typing_duration_seconds: typingSeconds,
                    assignment_id: assignmentId,
                    language: i18n.language,
                    sage_source: sageSource,
                },
            })

            if (res.error) throw res.error

            setResult(res.data)
            setStatus('done')
            onComplete?.(res.data.score, maxScore)
        } catch (err) {
            console.error('[ShortAnswerBox] grader error:', err)
            setStatus('error')
        }
    }

    const scorePercent = result ? Math.round((result.score / maxScore) * 100) : 0
    const scoreColor = scorePercent >= 80 ? '#4ade80' : scorePercent >= 60 ? accentColor : '#f87171'

    return (
        <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
                background: 'rgba(255,255,255,0.03)',
                border: `1px solid ${accentColor}30`,
                borderRadius: 20, padding: 24,
                marginTop: 24,
            }}
        >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <div style={{ background: `${accentColor}18`, padding: 8, borderRadius: 10 }}>
                    <PenLine size={18} color={accentColor} />
                </div>
                <div>
                    <p style={{ fontSize: 12, fontWeight: 800, color: accentColor, letterSpacing: '1px', margin: 0, textTransform: 'uppercase' }}>
                        {t('shortAnswer.title', 'Reflection Response')}
                    </p>
                    <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', margin: '2px 0 0 0' }}>
                        {t('shortAnswer.wordRange', { min: wordMin, max: wordMax, defaultValue: '{{min}}–{{max}} words' })}
                    </p>
                </div>
            </div>

            {/* Static Warmth Nudge */}
            <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.65)', margin: '0 0 14px 0', fontStyle: 'italic' }}>
                {t('shortAnswer.nudge', 'Optional — writing this in your own words locks in what you learned.')}
            </p>

            {/* Prompt */}
            <div style={{
                background: `${accentColor}08`, border: `1px solid ${accentColor}20`,
                borderRadius: 12, padding: '14px 16px', marginBottom: 16,
            }}>
                <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.82)', lineHeight: 1.6, margin: 0 }}>
                    {prompt}
                </p>
            </div>

            <AnimatePresence mode="wait">
                {status !== 'done' ? (
                    <motion.div key="editor" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        {/* Textarea */}
                        <textarea
                            value={text}
                            onChange={e => setText(e.target.value)}
                            onPaste={handlePaste}
                            disabled={status === 'submitting'}
                            placeholder={t('shortAnswer.placeholder', 'Write your thoughts here in your own words…')}
                            style={{
                                width: '100%', minHeight: 130, resize: 'vertical',
                                background: 'rgba(255,255,255,0.05)',
                                border: `1px solid ${pasteDetected ? '#fbbf2460' : 'rgba(255,255,255,0.1)'}`,
                                borderRadius: 12, padding: '14px 16px',
                                color: '#fff', fontSize: 14, lineHeight: 1.65,
                                fontFamily: 'inherit', outline: 'none',
                                transition: 'border-color 0.2s',
                                boxSizing: 'border-box',
                            }}
                        />

                        {/* Word count + warnings */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
                            <span style={{
                                fontSize: 12, fontWeight: 700,
                                color: !meetsMin ? '#f87171' : !meetsMax ? '#f87171' : '#4ade80',
                            }}>
                                {wordCount} / {wordMin}–{wordMax} {t('shortAnswer.words', 'words')}
                            </span>

                            {pasteDetected && (
                                <span style={{ fontSize: 11, color: '#fbbf24', display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <AlertTriangle size={12} />
                                    {t('shortAnswer.pasteWarning', 'Paste detected — make it your own')}
                                </span>
                            )}

                            <button
                                onClick={handleSubmit}
                                disabled={!meetsMin || !meetsMax || status === 'submitting'}
                                style={{
                                    marginLeft: 'auto',
                                    background: meetsMin && meetsMax
                                        ? `linear-gradient(135deg, ${accentColor}, ${accentColor}bb)`
                                        : 'rgba(255,255,255,0.08)',
                                    color: meetsMin && meetsMax ? '#08111e' : 'rgba(255,255,255,0.25)',
                                    border: 'none', borderRadius: 10, padding: '9px 18px',
                                    fontSize: 13, fontWeight: 800, cursor: meetsMin && meetsMax ? 'pointer' : 'not-allowed',
                                    display: 'flex', alignItems: 'center', gap: 7,
                                    transition: 'all 0.2s',
                                }}
                            >
                                {status === 'submitting'
                                    ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> {t('shortAnswer.grading', 'Grading…')}</>
                                    : <><Send size={14} /> {t('shortAnswer.submit', 'Submit')}</>
                                }
                            </button>
                        </div>

                        {status === 'error' && (
                            <p style={{ fontSize: 12, color: '#f87171', marginTop: 10 }}>
                                {t('shortAnswer.error', 'Something went wrong. Please try again.')}
                            </p>
                        )}
                    </motion.div>
                ) : (
                    /* ── Results card ── */
                    <motion.div
                        key="result"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        style={{
                            background: `${scoreColor}0a`, border: `1px solid ${scoreColor}30`,
                            borderRadius: 14, padding: '20px 20px 16px',
                        }}
                    >
                        {/* Score */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
                            <div style={{
                                width: 54, height: 54, borderRadius: '50%',
                                background: `${scoreColor}18`, border: `2px solid ${scoreColor}`,
                                display: 'flex', flexDirection: 'column',
                                alignItems: 'center', justifyContent: 'center',
                            }}>
                                <span style={{ fontSize: 18, fontWeight: 900, color: scoreColor, lineHeight: 1 }}>
                                    {result.score}
                                </span>
                                <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', fontWeight: 600 }}>
                                    /{maxScore}
                                </span>
                            </div>
                            <div>
                                <p style={{ fontSize: 14, fontWeight: 800, color: scoreColor, margin: 0 }}>
                                    {result.score_label} — {scorePercent}%
                                </p>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
                                    <CheckCircle size={12} color="#4ade80" />
                                    <span style={{ fontSize: 11, color: '#4ade80', fontWeight: 600 }}>
                                        {t('shortAnswer.submitted', 'Response submitted & graded')}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* AI Feedback */}
                        <p style={{
                            fontSize: 13, color: 'rgba(255,255,255,0.72)', lineHeight: 1.65,
                            marginBottom: 14, borderLeft: `3px solid ${scoreColor}`,
                            paddingLeft: 12,
                        }}>
                            {result.ai_feedback}
                        </p>

                        {/* Concepts demonstrated */}
                        {result.concepts_demonstrated?.length > 0 && (
                            <div style={{ marginBottom: 12 }}>
                                <p style={{ fontSize: 10, fontWeight: 800, color: 'rgba(255,255,255,0.28)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>
                                    {t('shortAnswer.demonstrated', 'Concepts Demonstrated')}
                                </p>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                    {result.concepts_demonstrated.map((c: string, i: number) => (
                                        <span key={i} style={{
                                            fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 100,
                                            background: 'rgba(74,222,128,0.08)', color: '#4ade80',
                                            border: '1px solid rgba(74,222,128,0.2)',
                                        }}>{c}</span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Follow-up question */}
                        {result.follow_up_question && (
                            <div style={{
                                background: 'rgba(255,255,255,0.04)', borderRadius: 10,
                                padding: '12px 14px', marginTop: 6,
                            }}>
                                <p style={{ fontSize: 10, fontWeight: 800, color: 'rgba(255,255,255,0.28)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>
                                    {t('shortAnswer.followUp', 'Think Deeper')}
                                </p>
                                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', lineHeight: 1.6, margin: 0, fontStyle: 'italic' }}>
                                    {result.follow_up_question}
                                </p>
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </motion.div>
    )
}
